/**
 * @module config/print
 * @description Print specifications, in one place, for both outputs:
 * browser printing (any device, any printer) and the print-shop PDF.
 *
 * Why these sizes: the card artwork is a 3:2 landscape object, so it scales
 * without reflow to every 3:2 trim. 4 × 6 in is the standard US postcard
 * (the size print shops sell as "postcard" and the largest size that mails
 * at the USPS postcard rate is 4.25 × 6 in). 6 × 9 in is the "jumbo"
 * postcard: the size of AFJ's original print file (_sources, 648 × 432 pt),
 * best for the Gala pledge card where people write by hand. Jumbo cards
 * mail at the letter rate.
 *
 * Print-shop file conventions (FedEx Office, Vistaprint, local shops):
 * a PDF at final size plus 0.125 in bleed on every side, 300 dpi, with
 * important content inside a 0.125 in safe zone. The card design already
 * keeps text more than 0.125 in from the trim at both sizes.
 */

export type PrintSizeId = "4x6" | "6x9";

export interface PrintSize {
    id: PrintSizeId;
    label: string;
    /** Landscape trim, inches. */
    widthIn: number;
    heightIn: number;
    /** What to order at the print shop. */
    orderAs: string;
    mailing: string;
}

export const PRINT_SIZES: Record<PrintSizeId, PrintSize> = {
    "4x6": {
        id: "4x6",
        label: "4 × 6 in · standard postcard",
        widthIn: 6,
        heightIn: 4,
        orderAs: "Postcards, 4 × 6 in, double-sided, full color, 14–16 pt cardstock (matte or uncoated back if you will write on it)",
        mailing: "Mails at the USPS postcard rate.",
    },
    "6x9": {
        id: "6x9",
        label: "6 × 9 in · jumbo postcard (original design size)",
        widthIn: 9,
        heightIn: 6,
        orderAs: "Postcards, 6 × 9 in (jumbo), double-sided, full color, 14–16 pt cardstock (matte or uncoated back if you will write on it)",
        mailing: "Mails at the USPS letter rate (larger than the postcard maximum).",
    },
};

export const DEFAULT_PRINT_SIZE_ID: PrintSizeId = "4x6";

/** Extra artwork past the trim on every side, for print-shop files. */
export const BLEED_IN = 0.125;

/** Raster resolution of the print-shop PDF. */
export const PRINT_DPI = 300;

export const isPrintSizeId = (value: unknown): value is PrintSizeId => value === "4x6" || value === "6x9";
